import Head from 'next/head'
import Script from 'next/script'
import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { getArticleDateTime } from 'utils/article-date-time'
import { getRequestJSON } from 'api/index/location-api'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { s3Static } from 'utils/constants'
import { ptSingaporeFaqs } from 'utils/faq'

import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerHead } from 'components/index/service-category/personal-trainer/PersonalTrainerHead'
import TrustedPartners from 'components/index/service-category/personal-trainer/TrustedPartners'
import TrainerValueProposition from 'components/index/service-category/personal-trainer/TrainerValueProposition'
import { PersonalTrainerBooking } from 'components/index/service-category/personal-trainer/PersonalTrainerBooking'
import { TrainerCustomerReviews } from 'components/index/service-category/personal-trainer/TrainerCustomerReviews'
import { PersonalTrainerWhyUs } from 'components/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { DarkFaq } from 'components/index/service-category/personal-trainer/DarkFaq'

import Link from 'next/link'
import { openModal } from 'service/modal-service'
import { FaCheckCircle, FaHeart, FaChild, FaStar, FaShieldAlt, FaUtensils, FaChartLine, FaQuestionCircle, FaDumbbell } from 'react-icons/fa'
import { localBusinessNode } from 'utils/local-business-schema'

// ─── Static Props ───
export async function getStaticProps() {
    const { modifiedDate, month, year } = getArticleDateTime()
    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

    return {
        props: {
            requestCategorySettings,
            countryName,
            modifiedDate,
            month,
            year,
            testimonials,
        },
        revalidate: 259200,
    }
}

// ─── Content Section ───
const PrenatalPostpartumContent = ({ s3Static }) => {
    return (
        <section className="relative bg-slate-900 py-12 md:py-20 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-pink-400/8 rounded-full blur-3xl" />

            <div className="relative z-10 max-w-5xl mx-auto px-4 space-y-16">

                {/* ── 1. What is Prenatal Training ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-rose-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-rose-500 to-pink-400 flex items-center justify-center shadow-lg">
                                <FaHeart className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Prenatal Personal Training — Safe, Supported &amp; Tailored for Pregnancy</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Staying active during pregnancy is one of the best things you can do for yourself and your baby — but it needs to be done 
                                <strong className="text-white/90"> safely and under proper guidance</strong>. Our certified prenatal personal trainers in Singapore 
                                specialise in designing workout programmes that support your changing body throughout each trimester.
                            </p>
                            <p>
                                From the first trimester through to your third, your trainer modifies exercises to accommodate your growing bump, shifting centre of 
                                gravity, and changing energy levels. You&apos;ll never be pushed beyond what&apos;s safe — every session is designed specifically for 
                                where you are in your pregnancy.
                            </p>
                            <p>
                                Prenatal training at StringsSG focuses on <strong className="text-orange-400">pelvic floor strengthening</strong>, maintaining core stability, 
                                improving posture, managing common pregnancy discomforts (back pain, swelling, fatigue), and building the stamina you&apos;ll need for labour 
                                and delivery.
                            </p>
                            <p className="text-white/80 font-medium">
                                Active pregnancies lead to better outcomes — for both mum and baby. Let us help you stay strong and comfortable throughout.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── Image 1 ── */}
                <div className="rounded-3xl overflow-hidden border border-white/10">
                    <Image
                        src={s3Static + "personal-trainer/pregnant-woman-working-out.jpg"}
                        alt="Pregnant woman working out with a personal trainer in Singapore"
                        width={1200} height={600}
                        className="w-full h-48 md:h-80 object-cover"
                    />
                </div>

                {/* ── 2. Trimester Guide ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-orange-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-lg">
                                <FaShieldAlt className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Your Training Journey — Trimester by Trimester</h3>
                        </div>
                        <p className="text-white/50 text-sm md:text-base mb-4">
                            Every pregnancy is different, and your training should reflect that. Here&apos;s how we adapt your sessions as your body changes:
                        </p>
                        <div className="space-y-6 text-white/60 text-sm md:text-base leading-relaxed">
                            {[
                                {
                                    tri: '1st Trimester (Weeks 1–12) — Building the Foundation',
                                    color: 'text-green-400',
                                    desc: 'Nausea, fatigue, and anxiety are common — and completely normal. We start gently with light cardio, pelvic floor activation, and breathing exercises. Sessions are shorter (30–40 min) to match your energy. The goal isn\'t intensity — it\'s establishing a safe, consistent routine that your body can build on.',
                                },
                                {
                                    tri: '2nd Trimester (Weeks 13–26) — The Sweet Spot',
                                    color: 'text-orange-400',
                                    desc: 'Energy returns, nausea fades, and most mums feel their best. This is when we ramp up — core stability, glute strengthening, posture correction, and low-impact cardio. Exercises are modified to avoid lying flat on the back. We also start preparing your body for the physical demands of the third trimester and labour.',
                                },
                                {
                                    tri: '3rd Trimester (Weeks 27–40) — Preparing for Birth',
                                    color: 'text-rose-400',
                                    desc: 'Your bump is bigger, your balance shifts, and everything feels heavier. Sessions focus on mobility, hip opening, breathing techniques for labour, pelvic floor preparation, and gentle strength maintenance. We listen to your body every session — some days you\'ll feel strong, others you\'ll need something lighter. Both are perfectly fine.',
                                },
                            ].map((item, i) => (
                                <div key={i} className="flex items-start gap-4 bg-white/5 rounded-2xl p-4 border border-white/10">
                                    <FaCheckCircle className={`${item.color} text-xl mt-0.5 shrink-0`} />
                                    <div>
                                        <p className={`font-bold ${item.color} mb-1`}>{item.tri}</p>
                                        <p>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </article>

                {/* ── 3. Postpartum Training ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-purple-500 to-violet-400 flex items-center justify-center shadow-lg">
                                <FaChild className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Postpartum Training — Rebuilding Strength After Birth</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                After giving birth, your body needs time, care, and the right kind of movement to recover properly. Jumping back into regular 
                                workouts too quickly — or doing the wrong exercises — can worsen diastasis recti, weaken the pelvic floor further, or cause injury.
                            </p>
                            <p>
                                Our <strong className="text-orange-400">postpartum personal trainers in Singapore</strong> are trained to guide new mothers through a 
                                safe, progressive return to fitness. Whether you had a vaginal birth or a C-section, we start where you are and build from there — 
                                never rushing, always listening to your body.
                            </p>
                            <p>
                                Postpartum training starts with <strong className="text-white/90">breath work and pelvic floor reconnection</strong>, then progressively 
                                introduces core rehabilitation, glute and hip strength, and eventually full-body conditioning. The timeline varies for every mum, 
                                and that&apos;s completely okay.
                            </p>
                            <p>
                                We also understand the realities of new motherhood — sessions are flexible, baby-friendly, and designed to fit around feeding schedules 
                                and sleep deprivation. Training at home is fully supported so you don&apos;t have to worry about childcare.
                            </p>
                            <p className="text-white/80 font-medium">
                                You grew and delivered a human being. Your recovery deserves the same dedication.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── Image 2 ── */}
                <div className="rounded-3xl overflow-hidden border border-white/10">
                    <Image
                        src={s3Static + "personal-trainer/mother-with-her-baby-daughter-practice-yoga-home.jpg"}
                        alt="Mother practicing postnatal yoga with her baby at home in Singapore"
                        width={1200} height={600}
                        className="w-full h-48 md:h-80 object-cover"
                    />
                </div>

                {/* ── 4. Why Choose Us ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-blue-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg">
                                <FaCheckCircle className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Why Mothers in Singapore Choose StringsSG</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <ul className="space-y-4">
                                {[
                                    {
                                        title: 'Certified Prenatal & Postnatal Trainers',
                                        desc: 'All our trainers who work with pregnant and postpartum clients hold recognised prenatal fitness certifications — not just general PT qualifications.',
                                    },
                                    {
                                        title: 'Free Consultation',
                                        desc: 'Like all StringsSG clients, you start with a completely free consultation — no strings attached. We assess your needs, discuss your goals, and design a personalised plan before you commit to anything.',
                                    },
                                    {
                                        title: 'Train at Home',
                                        desc: 'No need to travel to a gym during pregnancy or as a new mum. Your trainer comes to you — home, condo gym, or outdoor park — on your schedule.',
                                    },
                                    {
                                        title: 'Baby-Friendly Sessions',
                                        desc: 'Your baby is always welcome. Postpartum sessions can be structured around your baby\'s nap times or include them in light activities like mum-and-baby yoga flows.',
                                    },
                                    {
                                        title: 'Nutrition Guidance Included',
                                        desc: 'Every package includes a personalised diet plan — adapted for pregnancy calorie needs, breastfeeding, or postpartum recovery depending on your stage.',
                                    },
                                    {
                                        title: 'Progress Tracked Biweekly',
                                        desc: 'Your trainer checks in every two weeks to track measurements, energy levels, and fitness milestones — adjusting the programme as your body changes.',
                                    },
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <FaCheckCircle className="text-orange-400 mt-1 shrink-0" />
                                        <div>
                                            <strong className="text-white/90">{item.title}</strong>
                                            <span className="text-white/50"> — {item.desc}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </article>

                {/* ── 5. What To Expect ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-amber-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-800/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-amber-500 to-yellow-400 flex items-center justify-center shadow-lg">
                                <FaDumbbell className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Exercises Covered in Prenatal &amp; Postpartum Training</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                                    <h4 className="text-white/90 font-bold mb-3">🤰 Prenatal Focus</h4>
                                    <ul className="space-y-2">
                                        {[
                                            'Pelvic floor activation & Kegels',
                                            'Modified squats & lunges',
                                            'Supported core stabilisation',
                                            'Prenatal yoga & stretching',
                                            'Low-impact cardio (walking, swimming)',
                                            'Posture correction exercises',
                                            'Breathing & relaxation techniques',
                                        ].map((ex, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm">
                                                <FaCheckCircle className="text-rose-400 shrink-0" />
                                                {ex}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                                    <h4 className="text-white/90 font-bold mb-3">👶 Postpartum Focus</h4>
                                    <ul className="space-y-2">
                                        {[
                                            'Pelvic floor reconnection',
                                            'Diastasis recti rehabilitation',
                                            'Deep core & transverse abdominis',
                                            'Glute & hip strengthening',
                                            'Postnatal yoga & mobility',
                                            'Progressive strength training',
                                            'Mum-and-baby exercises',
                                        ].map((ex, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm">
                                                <FaCheckCircle className="text-purple-400 shrink-0" />
                                                {ex}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </article>

                {/* ── Image 3 ── */}
                <div className="rounded-3xl overflow-hidden border border-white/10">
                    <Image
                        src={s3Static + "personal-trainer/mother-practicing-yoga-with-her-baby-daughter-home.jpg"}
                        alt="Mother and baby doing postnatal yoga together at home"
                        width={1200} height={600}
                        className="w-full h-48 md:h-80 object-cover"
                    />
                </div>

                {/* ── 6. Real Talk — Common Concerns ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-teal-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-teal-500 to-cyan-400 flex items-center justify-center shadow-lg">
                                <FaQuestionCircle className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Real Talk — Your Concerns, Honestly Answered</h3>
                        </div>
                        <div className="space-y-5 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                We know that Googling &quot;is it safe to exercise during pregnancy&quot; leads to a rabbit hole of conflicting advice. 
                                Here are honest answers to the questions we hear most from mums in Singapore:
                            </p>
                            {[
                                {
                                    q: '"I\'m worried I\'ll hurt the baby if I exercise."',
                                    a: 'This is the most common fear — and the most unfounded. Research from ACOG (American College of Obstetricians & Gynecologists) consistently shows that moderate exercise during pregnancy is not only safe but actively beneficial. Your baby is well-protected by amniotic fluid, and your trainer will never include anything risky. That said, always get your OB-GYN\'s clearance first.',
                                },
                                {
                                    q: '"I wasn\'t active before pregnancy — is it too late to start?"',
                                    a: 'Absolutely not. Pregnancy is actually one of the best times to build healthy movement habits. We start very gently with first-time exercisers — no one expects you to do burpees at 20 weeks. Your trainer meets you where you are, whether that\'s walking, stretching, or light resistance work.',
                                },
                                {
                                    q: '"How soon after birth can I start training again?"',
                                    a: 'For vaginal births, most doctors clear light exercise at 6 weeks postpartum. For C-sections, it\'s typically 8–12 weeks. Your trainer will start with breath work and gentle pelvic floor reconnection before progressing to anything more demanding. We never rush postpartum recovery.',
                                },
                                {
                                    q: '"I have diastasis recti — can I still train?"',
                                    a: 'Yes — and you should. Diastasis recti (abdominal separation) affects up to 60% of postpartum women, and it won\'t fix itself without targeted rehab. Our trainers are experienced in diastasis recti recovery — focusing on deep core work, transverse abdominis activation, and modified stability exercises. Avoid crunches and planks until cleared.',
                                },
                                {
                                    q: '"I can barely sleep — how am I supposed to exercise?"',
                                    a: 'We get it. New motherhood is exhausting. That\'s why our sessions are flexible — if you need to reschedule, shorten, or just do gentle stretching instead of a full workout, that\'s completely fine. Most mums tell us they actually feel more energised and sleep better on days they train.',
                                },
                            ].map((item, i) => (
                                <div key={i} className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-2">
                                    <p className="text-orange-400 font-bold text-sm md:text-base">{item.q}</p>
                                    <p className="text-white/55 text-sm leading-relaxed">{item.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </article>

                {/* ── 7. Safety Note ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-green-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-green-500 to-emerald-400 flex items-center justify-center shadow-lg">
                                <FaShieldAlt className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Safety First — Always</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Before beginning any prenatal training programme, we strongly recommend obtaining clearance from your OB-GYN or midwife. 
                                Your trainer will review any medical notes and adapt the programme accordingly. Certain conditions — such as placenta praevia, 
                                pre-eclampsia, or cervical incompetence — may require a modified or paused programme.
                            </p>
                            <p>
                                For postpartum clients, we recommend waiting at least <strong className="text-white/90">6 weeks after vaginal birth</strong> or 
                                <strong className="text-white/90"> 8–12 weeks after C-section</strong> before resuming structured exercise, and again — only after 
                                your doctor has given the green light.
                            </p>
                            <p>
                                Our trainers are trained to recognise warning signs and will always err on the side of caution. Your health and your baby&apos;s 
                                health comes first. Always.
                            </p>
                            <div className="pt-2">
                                <Link
                                    href="/sg/personal-trainer/what-to-expect-from-personal-training"
                                    className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm transition-colors"
                                >
                                    See what&apos;s included in your consultation → 
                                </Link>
                            </div>
                        </div>
                    </div>
                </article>

                {/* ── Quick Summary Cards ── */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                        { icon: '🤰', label: 'Prenatal Training', sub: 'All 3 trimesters' },
                        { icon: '👶', label: 'Postpartum Training', sub: 'Safe progressive recovery' },
                        { icon: '🎁', label: 'Free Consultation', sub: 'No obligation' },
                        { icon: '🏠', label: 'Train at Home', sub: 'Baby-friendly sessions' },
                        { icon: '🥗', label: 'Nutrition Plan', sub: 'Pregnancy & breastfeeding' },
                        { icon: '📊', label: 'Progress Tracking', sub: 'Biweekly check-ins' },
                    ].map((item, i) => (
                        <div key={i} className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-4 text-center hover:bg-white/10 hover:border-rose-400/30 transition-all duration-300">
                            <span className="text-2xl block mb-1">{item.icon}</span>
                            <p className="text-sm font-semibold text-white/90">{item.label}</p>
                            <p className="text-[10px] text-white/40">{item.sub}</p>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    )
}


// ─── Main Page Component ───
export default function PrenatalPostpartumTraining(props) {
    const { requestCategorySettings, modifiedDate, year, testimonials } = props
    const router = useRouter()

    const [requestForm, setRequestForm] = useState(false)
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const requestType = "Fitness Trainer"

    useEffect(() => {
        if (router.query.requestForm === "true") {
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 })
        openModal(setRequestForm)
    }

    return (
        <>
            <Head>
                <title>Prenatal &amp; Postpartum Personal Trainer Singapore | StringsSG</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta httpEquiv="content-language" content="en-sg" />
                <meta name="description"
                    content="Certified prenatal and postpartum personal trainer in Singapore. Safe pregnancy workouts, postnatal recovery training, pelvic floor rehab, free consultation, home training available." />
                <meta name="keywords"
                    content="prenatal personal trainer singapore, postnatal personal trainer singapore, postpartum fitness trainer singapore, pregnancy workout singapore, pelvic floor trainer singapore, mummy fitness singapore" />
                <meta name="author" content="StringsSG" />
                <meta name="geo.region" content="SG" />
                <meta name="geo.placename" content="Singapore" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:url" content="https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content="Prenatal & Postpartum Personal Trainer Singapore | StringsSG" />
                <meta property="og:description" content="Certified prenatal and postpartum personal trainer in Singapore. Safe pregnancy workouts, postnatal recovery, pelvic floor rehab, free consultation, home training available." />
                <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/pregnant-woman-working-out.jpg" />
                <meta property="article:published_time" content="2026-03-20T07:00:00+00:00" />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content="Prenatal & Postpartum Personal Trainer Singapore | StringsSG" />
                <meta name="twitter:description" content="Safe pregnancy workouts, postnatal recovery training, pelvic floor rehab. Free consultation, home training available." />
                <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/pregnant-woman-working-out.jpg" />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training" />
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html:
                        `{"@context":"https://schema.org","@graph":[{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/","name":"Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/","name":"Prenatal Postpartum Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/"}}]},${JSON.stringify(localBusinessNode)},{"@type":"Service","@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/#service","offers":{"@type":"AggregateOffer","priceCurrency":"SGD","lowPrice":"84","highPrice":"110","availability":"https://schema.org/InStock"},"name":"Prenatal & Postpartum Personal Trainer Singapore","description":"Certified prenatal and postpartum personal training in Singapore. All 3 trimesters covered, postnatal recovery, pelvic floor rehab, baby-friendly home sessions, custom nutrition plans.","provider":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"areaServed":"Singapore","serviceType":"Prenatal Postpartum Personal Training"},{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/#webpage","url":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/","name":"Prenatal & Postpartum Personal Trainer Singapore | StringsSG","description":"Certified prenatal and postpartum personal trainer in Singapore. Safe pregnancy workouts, postnatal recovery training, pelvic floor rehab, free consultation, home training available.","inLanguage":"en-SG","isPartOf":{"@id":"https://www.stringssg.com/#website"},"breadcrumb":{"@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/#breadcrumblist"},"datePublished":"2026-03-20T07:00:00+00:00","dateModified":"${modifiedDate}","mainEntity":{"@id":"https://www.stringssg.com/sg/personal-trainer/prenatal-postpartum-training/#service"}},{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","foundingDate":"2018","sameAs":["https://www.facebook.com/StringsSG","https://www.linkedin.com/company/stringssg/","https://www.youtube.com/channel/UCOcyUdhVeOZf2kXTGtg9oHw/"]},{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG Singapore","inLanguage":"en-SG","publisher":{"@id":"https://www.stringssg.com/#organization"}}]}`
                    }}
                />
            </Head>

            <div>
                <RequestProvider>
                    {/* Hero */}
                    <PersonalTrainerHead
                        heroImage={s3Static + "personal-trainer/pregnant-woman-working-out.jpg"}
                        title="Prenatal & Postpartum Personal Trainer in Singapore"
                        heading="Prenatal & Postpartum Personal Trainer in Singapore"
                        heroCopy="Trimester-aware training and guided postnatal recovery from $84/session, delivered at home by certified specialists."
                        setIsQuickBook={setIsQuickBook}
                        openRequestForm={openRequestForm}
                        requestForm={requestForm}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={isCustomBook}
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                    />

                    {/* H1 Intro */}
                    <section className="relative bg-slate-900 py-10 md:py-16 overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-rose-500/15 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-400/10 rounded-full blur-3xl" />
                        <div className="relative z-10 max-w-5xl mx-auto px-2 text-center">
                            {/* <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight">
                                <strong>Prenatal &amp; Postpartum Personal Trainer<br />in Singapore</strong>
                            </h2> */}
                            <p className="text-white/50 text-base md:text-lg mx-auto leading-relaxed mb-4">
                                Your body is doing something incredible — growing, nurturing, and bringing new life into the world. 
                                You deserve a trainer who understands exactly what that means for your workouts, your recovery, and your confidence.
                            </p>
                            <p className="text-white/40 text-sm md:text-base mx-auto leading-relaxed mb-8">
                                Our certified prenatal and postnatal trainers work with expectant and new mothers across Singapore — 
                                at home, at your condo gym, or outdoors. Every session is adapted to your body, your trimester, and your comfort level.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                {['Free Consultation', 'All 3 Trimesters', 'Postnatal Recovery', 'Train at Home', 'Baby-Friendly'].map((tag, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full text-xs font-medium text-white/70">
                                        <FaStar className="text-rose-400 text-[10px]" />
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Main Content */}
                    <PrenatalPostpartumContent s3Static={s3Static} />

                    {/* Trusted Partners */}
                    {/* <TrustedPartners /> */}

                    {/* Value Proposition */}
                    <TrainerValueProposition />

                    {/* Why Us */}
                    <PersonalTrainerWhyUs />

                    {/* Packages */}
                    <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={2026} />

                    {/* Reviews */}
                    <TrainerCustomerReviews testimonials={testimonials} />

                    {/* FAQs */}
                    <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                        <div className="absolute top-0 left-0 w-72 h-72 bg-rose-500/8 rounded-full blur-3xl" />
                        <div className="relative z-10 max-w-5xl mx-auto px-4">
                            <DarkFaq
                                title="Prenatal & Postpartum Training FAQs"
                                questionsAnswers={ptSingaporeFaqs}
                            />
                        </div>
                    </section>

                </RequestProvider>
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
            <Script id="facebook-tracking" strategy="lazyOnload">
                {`!function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '128634041116395');
                    fbq('track', 'PageView');`}
            </Script>
        </>
    )
}
